// Challenge pieces shared by Rivals, Results, the game and the challenge page: the share grid from
// a pattern, W/L/D form pills, the challenge sheet, inviting rivals in the app, and the bar you play
// against.
import { useState, type CSSProperties } from 'react'
import type { Challenge, ChallengePlayer, HeadToHead, MatchResult, Me } from '@wbwl/contract'
import { ApiFailure, api } from '../lib/api.ts'
import { useRivals } from '../lib/data.ts'
import { fmt } from '../lib/game.ts'
import { challengeLine, copyText, nativeShare } from '../lib/share.ts'
import { invalidate, setResource } from '../lib/store.ts'
import { Icon } from './Icon.tsx'
import { Avatar, Sheet, useToast } from './shell.tsx'

export const challengeUrl = (code: string) => `${window.location.origin}/c/${code}`

/** "Talon", "Talon and Mia", "Talon, Mia and Jo". */
export function andList(names: readonly string[]): string {
  return names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** The share grid from a pattern ('b' burned, 'g' gold, '.' plain), with empty rows to make six. */
export function PatternGrid({ grid, m = 5 }: { grid: readonly string[]; m?: number }) {
  const cells = []
  for (let r = 0; r < 6; r++) {
    for (let c = 0; c < 5; c++) {
      const ch = grid[r]?.[c]
      cells.push(<i key={`${r}-${c}`} className={ch === undefined ? 'e' : ch === 'b' ? 'b' : ch === 'g' ? 'g' : ''} />)
    }
  }
  return <div className="mgrid" style={{ '--m': `${m}px` } as CSSProperties} aria-hidden="true">{cells}</div>
}

const RESULT_WORD = { w: 'won', l: 'lost', d: 'tied' } as const

/** Results as pills, oldest first: a gold W, a red-ringed L, a plain D. */
export function Form({ form, big, small }: { form: readonly MatchResult[]; big?: boolean; small?: boolean }) {
  return (
    <span className={`form${big ? ' big' : small ? ' sm' : ''}`} aria-label={`Latest, oldest first: ${form.map((r) => RESULT_WORD[r]).join(', ')}`}>
      {form.map((r, i) => <i key={i} className={r} aria-hidden="true">{r.toUpperCase()}</i>)}
    </span>
  )
}

/** Your latest result against someone in words, then the pills once there's a history to show. */
export function FormLine({ form }: { form: readonly MatchResult[] }) {
  if (form.length === 0) return null
  const last = form[form.length - 1]
  return (
    <span className="form-line">
      <span className={last}>{RESULT_WORD[last][0].toUpperCase()}{RESULT_WORD[last].slice(1)} last time</span>
      {form.length > 1 && <Form form={form} />}
    </span>
  )
}

export const matchCount = (r: HeadToHead) => r.wins + r.losses + r.ties

/** "2–3" (with ties, "2–3–1") and who leads it. */
export function recordText(r: HeadToHead, name: string): { score: string; lead: string } {
  return {
    score: r.ties ? `${r.wins}–${r.losses}–${r.ties}` : `${r.wins}–${r.losses}`,
    lead: r.wins > r.losses ? 'You lead' : r.wins < r.losses ? `${name} leads` : 'All square',
  }
}

/** "you lead 3–2", "Talon leads 3–2", "tied 2–2"; with `named`, "you lead Talon 3–2". */
export function recordPhrase(r: HeadToHead, name: string, named = false): string {
  const { score } = recordText(r, name)
  if (r.wins > r.losses) return named ? `you lead ${name} ${score}` : `you lead ${score}`
  if (r.wins < r.losses) return `${name} leads ${score}`
  return named ? `tied with ${name} ${score}` : `tied ${score}`
}

/** Who has finished, who hasn't, and who's on top. */
export function standingsOf(ch: Challenge) {
  const joined = ch.players.filter((p) => p.status === 'joined')
  const done = joined.filter((p) => p.done)
  const pending = joined.filter((p) => !p.done)
  const best = done.length ? Math.max(...done.map((p) => p.total ?? 0)) : null
  const winners = done.filter((p) => p.total === best)
  const mine = ch.players.find((p) => p.mine) ?? null
  // Your result, once you're in it, everyone in it has finished, and there's someone to beat.
  const verdict: 'won' | 'lost' | 'tied' | null = mine?.status === 'joined' && mine.done && pending.length === 0 && done.length > 1
    ? winners.includes(mine) ? (winners.length > 1 ? 'tied' : 'won') : 'lost'
    : null
  return { joined, done, pending, best, winners, mine, verdict }
}

/** Where a player is: their rank once finished, otherwise how far they've got. */
export function progressText(p: ChallengePlayer): string {
  if (p.done) return p.rankName ?? 'Finished'
  if (p.status === 'invited') return 'Invited'
  if (p.phase === 'phoenix') return 'Raising the phoenix'
  return p.rows > 0 ? `Playing · on row ${Math.min(p.rows + 1, 6)}` : 'Not started'
}

/** The score you're chasing in a challenge, and whose it is. */
export interface Target {
  readonly id: string
  readonly name: string
  readonly avatar: string | null
  readonly total: number
  readonly rankName: string | null
}

/**
 * The best finished score among the other players in your challenges (and, with `invites`, the
 * ones you've been invited to): the one to beat.
 */
export function targetOf(challenges: readonly Challenge[], invites = false): Target | null {
  let target: Target | null = null
  for (const ch of challenges) {
    if (ch.me === 'none' || (ch.me === 'invited' && !invites)) continue
    for (const p of ch.players) {
      if (!p.mine && p.done && p.total !== null && (!target || p.total > target.total)) {
        target = { id: p.id, name: p.displayName, avatar: p.avatar, total: p.total, rankName: p.rankName }
      }
    }
  }
  return target
}

/** The share of the target you've reached, for a progress track. */
export const chasePct = (total: number, target: Target) => `${Math.min(100, Math.round((total / Math.max(1, target.total)) * 100))}%`

// ---------- cards ----------

/** Under the score on a phone, while you play an edition someone has finished in your challenge. */
export function VsBar({ target, total }: { target: Target; total: number }) {
  const gap = total - target.total
  return (
    <div className={`vs-bar${gap > 0 ? ' ahead' : ''}`} aria-label={gap > 0 ? `You’re ahead of ${target.name} by ${gap}` : `${target.name} scored ${target.total}. You need ${1 - gap} more to beat it.`}>
      <div className="who"><span className="lbl">{gap > 0 ? 'Beating' : 'Beat'}</span><span className="ellipsis">{target.name}’s</span><b>{fmt(target.total)}</b></div>
      <div className="vs-track"><i style={{ width: chasePct(total, target) }} /></div>
      <div className="to">{gap > 0 ? <>Ahead by <b>{fmt(gap)}</b></> : gap === 0 ? <b>Level</b> : <><b>{fmt(1 - gap)}</b> to go</>}</div>
    </div>
  )
}

// ---------- the challenge sheet ----------

/**
 * The players you've had challenges with who aren't in this one yet, to invite in the app: who's
 * been sent it, and sending it.
 */
export function useRivalInvites(ch: Challenge | null, onChange: (ch: Challenge) => void) {
  const toast = useToast()
  const rivals = useRivals(ch !== null).data?.rivals
  const [sending, setSending] = useState<string | null>(null)
  async function invite(playerId: string) {
    if (!ch) return
    setSending(playerId)
    try {
      const updated = await api.post<Challenge>(`/challenges/${ch.code}/invite`, { playerId })
      setResource(`challenge:${ch.code}`, updated)
      invalidate('edchallenges:', 'mychallenges')
      onChange(updated)
    } catch (e) {
      toast(e instanceof ApiFailure && e.body.message ? e.body.message : 'Couldn’t send that')
    } finally {
      setSending(null)
    }
  }

  const statusOf = (id: string) => ch?.players.find((p) => p.id === id)?.status ?? null
  const pickable = (rivals ?? []).filter((r) => statusOf(r.player.id) !== 'joined')
  return { pickable, sent: (id: string) => statusOf(id) === 'invited', sending, invite }
}

/**
 * Sending a challenge: what your friends will see, the link to send or copy, and the players
 * you've had challenges with, to invite in the app.
 */
export function ChallengeSheet({ challenge: ch, me, onClose, onChange }: { challenge: Challenge | null; me: Me; onClose(): void; onChange(ch: Challenge): void }) {
  const toast = useToast()
  const { pickable, sent: invited, sending, invite } = useRivalInvites(ch, onChange)
  const mine = ch?.players.find((p) => p.mine)
  const url = ch ? challengeUrl(ch.code) : ''
  const text = ch && mine?.done ? challengeLine(ch.edition, mine.rankName!, mine.total!) : ''

  async function copy() {
    toast(await copyText(url) ? <><Icon name="check" />Link copied — paste it anywhere</> : 'Couldn’t copy. Select the link and copy it.')
  }
  async function send() {
    if ((await nativeShare(`${text} ${url}`)) === 'unavailable') await copy()
  }

  return (
    <Sheet open={ch !== null} onClose={onClose} label="Challenge a friend">
      {ch && (
        <div className="share">
          <div className="share-head">
            <h2>Challenge a friend</h2>
            <button type="button" className="ibtn boxed" aria-label="Close" onClick={onClose}><Icon name="close" /></button>
          </div>
          <div className="eyebrow">What they’ll see</div>
          <div className="card ch-preview">
            <Avatar id={me.id} name={me.displayName} src={me.avatar} size={40} />
            <div>
              <b>{me.displayName} challenged you</b>
              {mine?.done && <span className="faint mono small">{mine.rankName} · {fmt(mine.total!)} · No. {ch.edition}</span>}
            </div>
            {mine?.grid && <PatternGrid grid={mine.grid} m={7} />}
          </div>
          <div className="link-field">
            <Icon name="link" />
            <span className="ellipsis lf-url">{url.replace(/^https?:\/\//, '')}</span>
            <button type="button" className="btn ghost sm" onClick={copy}>Copy</button>
          </div>
          <button type="button" className="btn ink block" onClick={send}><Icon name="share" />Send the link</button>
          <p className="faint small sheet-note">Anyone with the link can join, so it works in a group chat too. It stays open for 7 days.</p>
          {pickable.length > 0 && (
            <div className="rivals-pick">
              <div className="eyebrow">Or send it to someone you’ve played</div>
              {pickable.map((r) => {
                const sent = invited(r.player.id)
                return (
                  <div className="rival-pick" key={r.player.id}>
                    <Avatar id={r.player.id} name={r.player.displayName} src={r.player.avatar} />
                    <div><b>{r.player.displayName}</b><span>{recordPhrase(r.record, r.player.displayName)} · last played No. {r.last.edition}</span></div>
                    {sent
                      ? <span className="btn sm done"><Icon name="check" />Sent</span>
                      : <button type="button" className="btn ghost sm" disabled={sending === r.player.id || !ch.open} onClick={() => invite(r.player.id)}>Send</button>}
                  </div>
                )
              })}
              <p className="faint small sheet-note">They’ll find it on their Today page next time they open the game.</p>
            </div>
          )}
        </div>
      )}
    </Sheet>
  )
}
